import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/use-auth'
import { manilaWallToIso } from '@/lib/ph'

export interface TimeEntry {
  id: string
  user_id: string
  task_id: number | null
  started_at: string
  ended_at: string | null
  notes: string | null
}

export const entryMinutes = (e: TimeEntry): number =>
  Math.max(0, Math.round(((e.ended_at ? new Date(e.ended_at).getTime() : Date.now()) - new Date(e.started_at).getTime()) / 60000))

export const fmtHours = (mins: number): string => {
  const h = Math.floor(mins / 60), m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/** Time entries for one job + the caller's running timer + clock on/off. */
export function useJobTime(taskId: number) {
  const { user, companyId } = useAuth()
  const qc = useQueryClient()

  const entries = useQuery({
    queryKey: ['time-entries', taskId],
    queryFn: async (): Promise<TimeEntry[]> => {
      const { data, error } = await supabase.from('time_entry')
        .select('id, user_id, task_id, started_at, ended_at, notes')
        .eq('task_id', taskId).order('started_at', { ascending: false }).limit(100)
      if (error) throw error
      return (data ?? []) as TimeEntry[]
    },
  })

  const running = useQuery({
    queryKey: ['running-timer'],
    queryFn: async (): Promise<TimeEntry | null> => {
      const { data, error } = await supabase.from('time_entry')
        .select('id, user_id, task_id, started_at, ended_at, notes')
        .eq('user_id', user!.id).is('ended_at', null).maybeSingle()
      if (error) throw error
      return (data as TimeEntry | null) ?? null
    },
    enabled: !!user,
    refetchInterval: 60000,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['time-entries'] })
    qc.invalidateQueries({ queryKey: ['running-timer'] })
    qc.invalidateQueries({ queryKey: ['timesheet'] })
  }

  const clockOn = useMutation({
    mutationFn: async () => {
      // close any stray running timer first, then start on this job
      await supabase.from('time_entry').update({ ended_at: new Date().toISOString() })
        .eq('user_id', user!.id).is('ended_at', null)
      const { error } = await supabase.from('time_entry')
        .insert({ company_id: companyId, user_id: user!.id, task_id: taskId })
      // 048 T033. SP001 is the health gate refusing the clock-on. The raw Postgres message is not
      // something a trade person on a roof can act on, so it is translated here rather than surfaced.
      if (error?.code === 'SP001' || /health check/i.test(error?.message ?? '')) {
        throw new Error('Answer the health check for this job first. It is in your notifications.')
      }
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const clockOff = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('time_entry').update({ ended_at: new Date().toISOString() })
        .eq('user_id', user!.id).is('ended_at', null)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { entries, running, clockOn, clockOff }
}

/** Date-range timesheet (admins see everyone; RLS limits techs to themselves). */
export function useTimesheet(from: string, to: string) {
  return useQuery({
    queryKey: ['timesheet', from, to],
    queryFn: async (): Promise<TimeEntry[]> => {
      const { data, error } = await supabase.from('time_entry')
        .select('id, user_id, task_id, started_at, ended_at, notes')
        .gte('started_at', manilaWallToIso(from, '00:00:00')!)
        .lte('started_at', manilaWallToIso(to, '23:59:59.999')!)
        .order('started_at', { ascending: true })
        .limit(1000)
      if (error) throw error
      return (data ?? []) as TimeEntry[]
    },
  })
}
